"use client";

export interface HTMLVideoElementWithWebkit extends HTMLVideoElement {
	webkitEnterFullscreen?: () => void;
	webkitExitFullscreen?: () => void;
	webkitDisplayingFullscreen?: boolean;
	webkitPresentationMode?: "inline" | "fullscreen" | "picture-in-picture";
	webkitSetPresentationMode?: (
		mode: "inline" | "fullscreen" | "picture-in-picture",
	) => void;
}

export interface HTMLElementWithWebkitFullscreen extends HTMLElement {
	webkitRequestFullscreen?: () => Promise<void> | void;
}

export function resolveMarketingVideoElement(
	shell: HTMLElement | null,
): HTMLVideoElement | null {
	if (shell == null) {
		return null;
	}
	const video = shell.querySelector("video");
	return video instanceof HTMLVideoElement ? video : null;
}

export function resolveMarketingVideoFullscreenTarget(
	shell: HTMLElement | null,
): HTMLElement | null {
	return resolveMarketingVideoElement(shell) ?? shell;
}

export function isMarketingVideoWebkitFullscreen(
	video: HTMLVideoElement | null,
): boolean {
	if (video == null) {
		return false;
	}
	const webkitVideo = video as HTMLVideoElementWithWebkit;
	if (webkitVideo.webkitDisplayingFullscreen === true) {
		return true;
	}
	return webkitVideo.webkitPresentationMode === "fullscreen";
}

export function isMarketingVideoFullscreenActive(
	shell: HTMLElement | null,
): boolean {
	if (shell == null || typeof document === "undefined") {
		return false;
	}

	try {
		const video = resolveMarketingVideoElement(shell);
		if (isMarketingVideoWebkitFullscreen(video)) {
			return true;
		}
		const fs = document.fullscreenElement;
		if (fs == null) {
			return false;
		}
		return fs === shell || shell.contains(fs);
	} catch {
		return false;
	}
}

export async function requestMarketingVideoFullscreen(
	shell: HTMLElement | null,
): Promise<boolean> {
	const target = resolveMarketingVideoFullscreenTarget(shell);
	if (target == null) {
		return false;
	}

	const video =
		target instanceof HTMLVideoElement
			? target
			: resolveMarketingVideoElement(shell);
	const webkitVideo = video as HTMLVideoElementWithWebkit | null;

	if (webkitVideo != null) {
		if (typeof webkitVideo.webkitSetPresentationMode === "function") {
			try {
				webkitVideo.webkitSetPresentationMode("fullscreen");
				return true;
			} catch {
				/* fall through */
			}
		}
		if (typeof webkitVideo.webkitEnterFullscreen === "function") {
			try {
				webkitVideo.webkitEnterFullscreen();
				return true;
			} catch {
				/* fall through */
			}
		}
	}

	const element = target as HTMLElementWithWebkitFullscreen;
	try {
		if (typeof element.requestFullscreen === "function") {
			await element.requestFullscreen();
			return true;
		}
		if (typeof element.webkitRequestFullscreen === "function") {
			await element.webkitRequestFullscreen();
			return true;
		}
	} catch {
		return false;
	}

	return false;
}

export async function exitMarketingVideoFullscreen(
	shell: HTMLElement | null,
): Promise<void> {
	const video = resolveMarketingVideoElement(shell);
	const webkitVideo = video as HTMLVideoElementWithWebkit | null;
	if (webkitVideo != null) {
		if (isMarketingVideoWebkitFullscreen(video)) {
			if (typeof webkitVideo.webkitSetPresentationMode === "function") {
				try {
					webkitVideo.webkitSetPresentationMode("inline");
					return;
				} catch {
					/* fall through */
				}
			}
			if (typeof webkitVideo.webkitExitFullscreen === "function") {
				try {
					webkitVideo.webkitExitFullscreen();
					return;
				} catch {
					/* fall through */
				}
			}
		}
	}

	try {
		if (document.fullscreenElement != null) {
			await document.exitFullscreen();
		}
	} catch {
		/* noop */
	}
}
